import { CampaignDisplayStatus, CampaignSummary } from "@signalist/shared-types";

/**
 * Status filter of the Campaigns overview (PREC-3880). Options map onto the derived
 * `CampaignDisplayStatus`: RUNNING → ACTIVE, DRAFT groups the not-yet-started states
 * (VALID / READY / LOADING), the rest map 1:1.
 */
export enum CampaignStatusFilter {
	ALL = "ALL",
	RUNNING = "RUNNING",
	PAUSED = "PAUSED",
	ENDED = "ENDED",
	DRAFT = "DRAFT",
	INVALID = "INVALID",
}

export enum CampaignChannelFilter {
	ALL = "ALL",
	EMAIL = "EMAIL",
	LINKEDIN = "LINKEDIN",
}

const DRAFT_STATUSES: CampaignDisplayStatus[] = [
	CampaignDisplayStatus.VALID,
	CampaignDisplayStatus.READY,
	CampaignDisplayStatus.LOADING,
];

export const matchesStatusFilter = (
	campaign: CampaignSummary,
	filter: CampaignStatusFilter
): boolean => {
	switch (filter) {
		case CampaignStatusFilter.ALL:
			return true;
		case CampaignStatusFilter.RUNNING:
			return campaign.status === CampaignDisplayStatus.ACTIVE;
		case CampaignStatusFilter.PAUSED:
			return campaign.status === CampaignDisplayStatus.PAUSED;
		case CampaignStatusFilter.ENDED:
			return campaign.status === CampaignDisplayStatus.ENDED;
		case CampaignStatusFilter.DRAFT:
			return DRAFT_STATUSES.includes(campaign.status);
		case CampaignStatusFilter.INVALID:
			return campaign.status === CampaignDisplayStatus.INVALID;
	}
};

export const matchesChannelFilter = (
	campaign: CampaignSummary,
	filter: CampaignChannelFilter
): boolean => {
	if (filter === CampaignChannelFilter.ALL) {
		return true;
	}

	return campaign.channels.includes(filter === CampaignChannelFilter.EMAIL ? "email" : "linkedin");
};

/** The campaigns of one list that pass the active status + channel filters. */
export const filterListCampaigns = (
	campaigns: CampaignSummary[],
	status: CampaignStatusFilter,
	channel: CampaignChannelFilter
): CampaignSummary[] =>
	campaigns.filter(
		(campaign) => matchesStatusFilter(campaign, status) && matchesChannelFilter(campaign, channel)
	);

/**
 * Whether a list row stays visible under the active campaign filters. With no filter every list
 * shows; with a filter, a list shows only while it still has a matching campaign — except that a
 * list whose summaries have not loaded yet stays visible (they are folded into the lean payload,
 * so this is a transient state, not a mismatch).
 */
export const shouldShowList = (
	campaigns: CampaignSummary[] | undefined,
	status: CampaignStatusFilter,
	channel: CampaignChannelFilter
): boolean => {
	if (status === CampaignStatusFilter.ALL && channel === CampaignChannelFilter.ALL) {
		return true;
	}

	if (!campaigns) {
		return true;
	}

	return filterListCampaigns(campaigns, status, channel).length > 0;
};

/** Stable selection key of one campaign across lists (a UUID may repeat after list duplication). */
export const campaignSelectionKey = (listId: number, campaignId: string): string =>
	`${listId}:${campaignId}`;
